import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  withAnalytics,
  SendAnalyticsType,
} from '@wildberries/wb-front-analytics-tracker';
import {
  initLoadManagerActionSaga,
  IReduxAction,
  Action,
} from '@mihanizm56/redux-core-modules';
import { selectedSupplierIdSelector } from '@wildberries/databus-service-suppliers';
import { ANALYTICS } from '@/pages/supplies-management/_constants';
import { TReduxStorage } from '@/pages/supplies-management/_types/redux-storage';
import {
  TSearchMultiInputOptionsArray,
  TWarehousesLimitsStorage,
} from '@/pages/supplies-management/children/warehouses-limits/redux/warehouses-limits-module/types';
import {
  areLimitsLoadingSelector,
  areWarehousesLoadingSelector,
  limitsWarehouseDateSelector,
  setSelectedLimitsWarehouseDateAction,
  warehousesSelector,
  setFilteredWarehousesAction,
  filteredWarehousesSelector,
  coefficientsReportSelector,
  setFilteredCoefficientsReportAction,
  filteredCoefficientsReportSelector,
  allowedDatesSelector,
  costForPaletteSelector,
} from '@/pages/supplies-management/children/warehouses-limits/redux/warehouses-limits-module';
import { filteredWarehousesSearch } from '@/pages/supplies-management/_utils/filtered-warehouses-search';
import { addCalendarDays } from '@/pages/supplies-management/_utils/add-days';
import { commentTextSelector } from '@/pages/supplies-management/_components/connected-ban-modal/redux/supplies-ban-module';
import { getCoefficientsReportConfig } from './_utils/get-coefficients-report-config';
import { getFormattedFilteredCoefficientsReport } from './_utils/get-formatted-filtered-coefficients-report';
import { WarehousesLimitsView } from './_components/warehouses-limits-view';
import {
  WAREHOUSE_FILTER_ALL,
  WAREHOUSE_ID_ALL,
  WAREHOUSES_FILTERS,
} from './_constants';

type TProps = {
  areWarehousesLoading: boolean;
  warehousesOptions: ReturnType<typeof warehousesSelector>;
  selectedDate: Date;
  initLoadManager: typeof initLoadManagerActionSaga;
  onSetSelectedLimitsWarehouseDate: IReduxAction<Date, string>;
  areLimitsLoading: boolean;
  filteredCoefficientsReportData: TWarehousesLimitsStorage['filteredCoefficientsReport'];
  filteredWarehouses: TWarehousesLimitsStorage['filteredWarehouses'];
  setFilteredWarehouses: Action<TSearchMultiInputOptionsArray>;
  setFilteredCoefficientsReport: Action<
    TWarehousesLimitsStorage['filteredCoefficientsReport']
  >;
  coefficientsReportData: TWarehousesLimitsStorage['coefficientsReport'];
  allowedDates: TWarehousesLimitsStorage['allowedDates'];
  banText: string;
  sendAnalytics: SendAnalyticsType;
  costForPalette: number;
};

type TUpdateCoefficient = {
  date?: Date;
};

class WrappedContainer extends Component<TProps> {
  state = {
    isHandledWarehouses: false,
  };

  componentDidUpdate() {
    const selectedWarehouse = JSON.parse(
      localStorage.getItem(WAREHOUSES_FILTERS),
    );

    if (
      this.props.warehousesOptions.length &&
      !this.state.isHandledWarehouses
    ) {
      const idSuppler = selectedSupplierIdSelector();
      this.state.isHandledWarehouses = true;
      this.handleChooseWarehouse(selectedWarehouse);

      const selectedWarehousesName = selectedWarehouse
        ?.map((warehouse) => warehouse?.label)
        .join(',');

      this.props.sendAnalytics({
        name: ANALYTICS.SupplierShipmentScreenCoeffV,
        params: {
          idSuppler,
          name: selectedWarehousesName,
        },
      });
    }
  }

  handleChooseWarehouse = (options: TSearchMultiInputOptionsArray | null) => {
    const isAllFiltersChosen =
      !options ||
      !options.length ||
      options.slice(-1)[0].value === WAREHOUSE_ID_ALL;

    const selectedWarehouse = JSON.parse(
      localStorage.getItem(WAREHOUSES_FILTERS),
    );

    if (options?.length !== selectedWarehouse?.length) {
      const selectedWarehousesName = options[options.length - 1]?.label;
      const idSuppler = selectedSupplierIdSelector();
      this.props.sendAnalytics({
        name: ANALYTICS.SupplierShipmentWarehouseCoeffT,
        params: {
          idSuppler,
          name: selectedWarehousesName,
        },
      });
    }

    if (isAllFiltersChosen) {
      localStorage.setItem(
        WAREHOUSES_FILTERS,
        JSON.stringify([WAREHOUSE_FILTER_ALL]),
      );
      const filteredCoefficients = getFormattedFilteredCoefficientsReport(
        [WAREHOUSE_FILTER_ALL],
        this.props.coefficientsReportData,
      );
      this.props.setFilteredCoefficientsReport(filteredCoefficients);
    } else {
      options.forEach((el, index) => {
        if (el.value === WAREHOUSE_ID_ALL) options.splice(index, 1);
      });
      localStorage.setItem(WAREHOUSES_FILTERS, JSON.stringify(options));
      const filteredCoefficients = getFormattedFilteredCoefficientsReport(
        options,
        this.props.coefficientsReportData,
      );
      this.props.setFilteredCoefficientsReport(filteredCoefficients);
      this.props.setFilteredWarehouses(this.props.warehousesOptions);
    }
  };

  handleUpdateCoefficient = ({ date }: TUpdateCoefficient) => {
    this.props.initLoadManager({
      requestConfigList: getCoefficientsReportConfig({
        date,
        setReport: this.props.setFilteredCoefficientsReport,
      }),
    });
  };

  handleChooseDate = (date: Date) => {
    const filters = JSON.parse(localStorage.getItem(WAREHOUSES_FILTERS));
    if (filters && filters[0].id && date) {
      const newDate = addCalendarDays({
        amount: date,
        step: 'day',
        days: 1,
      });

      this.handleUpdateCoefficient({
        date: newDate,
      });

      this.props.onSetSelectedLimitsWarehouseDate(date);
    }
  };

  handleOnSearchChange = (value: string) => {
    const filtered = filteredWarehousesSearch({
      warehouses: this.props.warehousesOptions,
      value,
    });
    this.props.setFilteredWarehouses(filtered);
  };

  render() {
    const selectedWarehouse = JSON.parse(
      localStorage.getItem(WAREHOUSES_FILTERS),
    );

    return (
      <WarehousesLimitsView
        allowedDates={this.props.allowedDates}
        areLimitsLoading={this.props.areLimitsLoading}
        areWarehousesLoading={this.props.areWarehousesLoading}
        banText={this.props.banText}
        coefficientsReportData={this.props.filteredCoefficientsReportData}
        costForPalette={this.props.costForPalette}
        filteredWarehouses={this.props.filteredWarehouses}
        onChangeDate={this.handleChooseDate}
        onSearchChange={this.handleOnSearchChange}
        onSelectWarehouse={this.handleChooseWarehouse}
        selectedDate={this.props.selectedDate}
        selectedWarehouse={selectedWarehouse}
      />
    );
  }
}

const mapStateToProps = (state: TReduxStorage) => ({
  warehousesOptions: warehousesSelector(state),
  coefficientsReportData: coefficientsReportSelector(state),
  filteredCoefficientsReportData: filteredCoefficientsReportSelector(state),
  areWarehousesLoading: areWarehousesLoadingSelector(state),
  selectedDate: limitsWarehouseDateSelector(state),
  areLimitsLoading: areLimitsLoadingSelector(state),
  filteredWarehouses: filteredWarehousesSelector(state),
  allowedDates: allowedDatesSelector(state),
  banText: commentTextSelector(state),
  costForPalette: costForPaletteSelector(state),
});

const mapDispatchToProps = {
  initLoadManager: initLoadManagerActionSaga,
  onSetSelectedLimitsWarehouseDate: setSelectedLimitsWarehouseDateAction,
  setFilteredWarehouses: setFilteredWarehousesAction,
  setFilteredCoefficientsReport: setFilteredCoefficientsReportAction,
};

export const ConnectedWarehousesLimits = compose(
  withAnalytics,
  connect(mapStateToProps, mapDispatchToProps),
)(WrappedContainer);
